<template>
  <div class="select">
    <div class="dropdown">
      <Popper
        v-model:open="open"
        :disabled="disabled"
        placement="bottom-end"
      >
        <template #trigger>
          <div class="dropdown__trigger">
            <div class="select__trigger">
              <div class="select__label-wrapper">
                <Loader
                  v-if="!regionTree"
                  class="select__loader"
                />
                <template v-else>
                  <span
                    :class="{
                      select__label: true,
                      'select__label--placeholder': modelValue.length === 0,
                    }"
                  >
                    {{ name }}
                  </span>
                  <span
                    v-if="showAdditionalCount"
                    class="select__filter-count typo-smallish"
                  >
                    + {{ additionalCount }}
                  </span>
                </template>
              </div>
              <div class="select__arrow">
                <UseSvg
                  class="icon icon--small"
                  name="arrow_down_16"
                />
              </div>
            </div>
          </div>
        </template>

        <div class="menu menu--dropdown menu--short menu--with-actions">
          <div class="menu__actions">
            <button
              class="button button--small button--light typo-ignore typo-small"
              type="button"
              @click="clearSelection"
            >
              {{ $t('general.clear_selection') }}
            </button>
            <button
              class="button button--small button--primary typo-ignore typo-small"
              type="submit"
              @click="applySelection"
            >
              {{ $t('general.apply') }}
            </button>
          </div>
          <RegionTree
            v-if="regionTree"
            v-model:allRegionsSelected="allRegionsSelected"
            :checkBoxes="true"
            class="menu__items"
            :regionTree="regionTree"
          />
          <Loader
            v-if="!regionTree"
            class="menu__loader"
          />
        </div>
      </Popper>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import booleanContains from '@turf/boolean-contains';
import I18n from 'i18n-js';

import useAjax from '@/functions/use_ajax';
import { compareByAreaAndId } from '@/functions/compare_regions';
import { rgeoPolygonToGeoJSON } from '@/functions/geospatial_conversions';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Loader from '@/components/generic/loader.vue';
import RegionTree from '@/components/regions/region_tree.vue';

const props = defineProps({
  placeholder: { type: String, default: '' },
  modelValue: {
    /** @type {import('@vue/runtime-core').PropType<string[]>} */
    type: Array,
    default: () => []
  },
  disabled: Boolean,
  endpointIdentifier: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const route = useRoute();
const ajax = useAjax();

const regionTree = ref([]);
const allRegionsSelected = ref(false);
const open = ref(false);

const name = computed(() => {
  if (props.modelValue.length === 0) return props.placeholder;
  if (props.modelValue[0] === 'all') return I18n.t('regions.all_regions');
  if (!regionTree.value) return '';
  return findRegion(props.modelValue[0])?.name;
});

/**
 * Whether or not to show the additional count tag next to the label
 * @type {import('@vue/reactivity').ComputedRef<boolean>}
 */
const showAdditionalCount = computed(() => (
  props.modelValue.length > 1 &&
    props.modelValue[0] !== 'all'
));

/**
 * The number to put in the additional count tag next to the label
 * @type {import('@vue/reactivity').ComputedRef<number>}
 */
const additionalCount = computed(() => (
  props.modelValue.length - 1
));

onMounted(async () => {
  let endpoint = props.endpointIdentifier;
  if (!endpoint) endpoint = `operations/${route.params.operationId}`;

  let { ok, data } = await ajax(`/organisations/${route.params.organisationId}/${endpoint}/regions`);

  if (ok) {
    regionTree.value = createRegionTree(data.regions);
    removeOldRegions(data.regions);
    resetSelected();
  }
});

watch(() => props.modelValue, resetSelected);
watch(open, resetSelected);

/**
 * Removes any selected region IDs that are no longer available (e.g. they were deleted)
 */
function removeOldRegions(data) {
  if (props.modelValue.length > 0) {
    let filteredRegions = props.modelValue.filter(id => id === 'all' || data.find(x => x.id === id));
    if (filteredRegions.length !== props.modelValue.length) {
      emit('update:modelValue', filteredRegions);
    }
  }
}

/**
 * Resets the selection (check-box states) to the state of the model value
 */
function resetSelected() {
  if (!regionTree.value) return;
  eachRegion(region => {
    region.selected = props.modelValue.includes(region.id);
  });
  allRegionsSelected.value = props.modelValue.includes('all');
}

/**
 * Builds the region tree from the given list of regions
 */
function createRegionTree(regions) {
  regions.forEach(region => {
    region.parent = null;
    region.children = [];
    region.geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  });
  regions.sort(compareByAreaAndId);
  regions
    .forEach(region => {
      let x = regions
        .filter(other => {
          return other.id !== region.id &&
            booleanContains(region.geoJSON, other.geoJSON) &&
            !booleanContains(other.geoJSON, region.geoJSON); // check that they don't contain each other (same poly)
        });
      x.forEach(other => {
        other.parent = region;
      });
    });
  regions.forEach(region => {
    if (region.parent) region.parent.children.push(region);
  });
  return regions.filter(region => !region.parent);
}

/**
 * Loops through all regions in the tree, invoking the given fn once for each
 * Breaks out of the loop if `false' is explicitly returned in the callback.
 */
function eachRegion(fn, regions = regionTree.value) {
  for (let region of regions) {
    if (fn(region) === false) return false;
    if (region.children) {
      if (eachRegion(fn, region.children) === false) return false;
    }
  }
  return true;
}

/**
 * Finds the region object in the tree with the given id
 */
function findRegion(id) {
  let found = null;
  eachRegion(region => {
    if (region.id === id) {
      found = region;
      return false;
    }
  });
  return found;
}

/**
 * Returns the list of ids that are currently selected in the popup.
 * @returns {string[]}
 */
function selectedIds() {
  if (!regionTree.value) {
    return props.modelValue;
  }
  let ids = allRegionsSelected.value ? ['all'] : [];
  eachRegion(region => {
    if (region.selected) {
      ids.push(region.id);
    }
  });
  return ids;
}

/**
 * Click handler for apply button.
 * Updates the model value and closes the modal.
 */
function applySelection() {
  emit('update:modelValue', selectedIds());
  open.value = false;
}

/**
 * Deselects everything.
 * Updates the model to a blank value, and closes the modal
 */
function clearSelection() {
  allRegionsSelected.value = false;
  if (regionTree.value) {
    eachRegion(region => {
      region.selected = false;
    });
  }
  emit('update:modelValue', []);
  open.value = false;
}
</script>
